import { guide, readMs } from "./guide/guide";
import { guideSurface } from "./guide/guideSurface";

/** How long a plain toast stays up at least; a longer line gets its reading time (guide.ts readMs). */
const DEFAULT_MS = 3500;
/** How long a toast with an action (an Undo) stays up: long enough to read it and reach the button. */
const ACTION_MS = 5000;
/** The drop-in starts a frame after insertion so the transition runs; the fade-out's length before removal. */
const SHOW_DELAY_MS = 20;
const HIDE_MS = 400;
/** A plain toast waits behind a guide bubble (DESIGN §4.2.7), re-checking this often, and shows anyway after the limit. */
const GUIDE_POLL_MS = 250;
const GUIDE_WAIT_MS = 10000;
/** How often a toast with an owner checks that its owner is still on the page. */
const OWNER_POLL_MS = 250;

/**
 * What a top toast must not land on: an open sheet's header (its title and close), and the hub's chips under the top
 * bar (the event chip, the builder and Tribute chips). A toast that would cover one drops below it.
 */
const TOAST_AVOID = ".sheet .sheethead, .hub .chips > .evhubchip:not([hidden]), .hub .chips > .bchip:not(.hidden), .hub .chips > .tchip:not(.hidden)";
/** The gap kept between a toast and what it steps below (px). */
const AVOID_GAP_PX = 8;

/** Moves a just-shown top toast below the headers and chips it would cover (bottom-anchored toasts are left alone). */
function clearOfHeaders(el: HTMLElement): void {
  if (typeof el.getBoundingClientRect !== "function" || typeof getComputedStyle !== "function") return;   // no layout (tests)
  const box = el.getBoundingClientRect();
  if (box.height === 0 || box.top > innerHeight / 2) return;
  const top0 = parseFloat(getComputedStyle(el).top);
  if (!Number.isFinite(top0)) return;
  let top = top0;
  const hits = [...document.querySelectorAll<HTMLElement>(TOAST_AVOID)].map((a) => a.getBoundingClientRect())
    .filter((r) => r.height > 0 && r.bottom > 0 && r.top < innerHeight / 2 && r.left < box.right && r.right > box.left)
    .sort((a, b) => a.top - b.top);
  for (const r of hits) if (r.top < top + box.height && r.bottom > top) top = r.bottom + AVOID_GAP_PX;
  if (top !== top0) el.style.top = `${Math.round(top)}px`;
}

/** A button on the toast (e.g. Undo): tapping it runs `onTap` and closes the toast. */
export interface ToastAction { readonly label: string; readonly onTap: () => void }
export interface ToastOptions {
  /** Time on screen (default: the line's reading time, at least 3.5 s; 5 s with an action). */
  readonly ms?: number;
  readonly action?: ToastAction;
  /**
   * What the toast belongs to (a sheet): it closes when that leaves the page. The Armoury's "30 items equipped · UNDO"
   * outlived the Armoury and sat over the More sheet opened next (UI design system v2, O3). Default: a toast with an
   * action belongs to the top open sheet under `parent` (its Undo means nothing once that sheet is gone); a plain toast
   * to nothing. `null` opts out.
   */
  readonly owner?: Element | null;
}
/** Closes the toast early (e.g. when the thing its Undo would restore has changed). Safe to call more than once. */
export interface ToastHandle { dismiss(): void }

/** The toasts on screen or waiting to show, with where they show: `dismissToasts` closes them. */
const live = new Map<ToastHandle, HTMLElement>();

/** Closes every toast under `parent` (all of them without one): for a screen change that retires what they said. */
export function dismissToasts(parent?: HTMLElement): void {
  for (const [h, p] of [...live]) if (!parent || p === parent) h.dismiss();
}

/** The top open sheet directly under `parent` (a stacked sheet below another is hidden, not removed), or null. */
function topSheet(parent: HTMLElement): Element | null {
  if (typeof parent.querySelectorAll !== "function") return null;   // no DOM (tests)
  const open = [...parent.querySelectorAll<HTMLElement>(":scope > .sheet")].filter((s) => s.style.visibility !== "hidden");
  return open.length > 0 ? open[open.length - 1] : null;
}

/**
 * In-app toast on a navy plate (P1.5.11): the web build has no local notifications, so returning players see this
 * instead. The third argument is the duration in ms, or options with an action button (44 px target, the only part
 * of a toast that takes taps; the rest lets taps through as before).
 *
 * Captain Bram's guide owns the player's attention while a bubble is up: a plain toast waits for it (polled, at most
 * GUIDE_WAIT_MS), and a toast shown while a bubble is docked in the top slot drops below it. A toast with an action
 * (Undo) never waits: the player just did the thing it undoes.
 */
export function toast(parent: HTMLElement, text: string, opts: number | ToastOptions = {}): ToastHandle {
  const o: ToastOptions = typeof opts === "number" ? { ms: opts } : opts;
  const el = document.createElement("div");
  el.className = o.action ? "toast plate act" : "toast plate";
  let closed = false;
  let hideTimer = 0;
  let waitTimer = 0;
  let ownerTimer = 0;
  const ms = o.ms ?? (o.action ? ACTION_MS : Math.max(DEFAULT_MS, readMs(text)));
  const owner = o.owner !== undefined ? o.owner : o.action ? topSheet(parent) : null;
  function dismiss(): void {
    if (closed) return;
    closed = true;
    live.delete(handle);
    window.clearTimeout(hideTimer);
    window.clearTimeout(waitTimer);
    window.clearTimeout(ownerTimer);
    el.classList.remove("show");
    window.setTimeout(() => el.remove(), HIDE_MS);
  }
  const handle: ToastHandle = { dismiss };
  live.set(handle, parent);
  // The owner is polled, not observed: a toast lives seconds, and a sheet can leave by any path (×, a tab, a redraw).
  const watchOwner = () => {
    if (closed || !owner) return;
    if (!owner.isConnected) { dismiss(); return; }
    ownerTimer = window.setTimeout(watchOwner, OWNER_POLL_MS);
  };
  watchOwner();
  if (o.action) {
    const label = document.createElement("span");
    label.textContent = text;
    const btn = document.createElement("button");
    btn.className = "toastact";
    btn.textContent = o.action.label;
    const action = o.action;
    btn.addEventListener("click", () => { if (closed) return; dismiss(); action.onTap(); });
    el.append(label, btn);
  } else {
    el.textContent = text;
  }
  function show(): void {
    if (closed) return;
    if (guideSurface().topClear !== null) el.classList.add("below-guide");
    parent.append(el);
    clearOfHeaders(el);
    hideTimer = window.setTimeout(() => dismiss(), ms);
    window.setTimeout(() => { if (!closed) el.classList.add("show"); }, SHOW_DELAY_MS);
  }
  const guideSpeaking = () => guide() !== null && guideSurface().blocking;
  if (o.action || !guideSpeaking()) show();
  else {
    let waited = 0;
    const poll = () => {
      waited += GUIDE_POLL_MS;
      if (!guideSpeaking() || waited >= GUIDE_WAIT_MS) show();
      else waitTimer = window.setTimeout(poll, GUIDE_POLL_MS);
    };
    waitTimer = window.setTimeout(poll, GUIDE_POLL_MS);
  }
  return handle;
}
